import { useEffect, useState } from 'react';
import { updateProfile, useCurrentUserProfile } from '@busy/api';
import { useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';

// Settings → Daily lesson time panel.
// Edits the user's lesson delivery schedule on `users`:
//   daily_lesson_time / delivery_days / timezone / quiet_enabled / quiet_start / quiet_end
// Onboarding step 4 sets the initial values; this is the long-term edit
// surface. Real delivery wiring is v1.1 — toggles + values persist now.

const DAYS = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'] as const;
const DAY_KEYS: Record<(typeof DAYS)[number], string> = {
  M: 'onb.dow.m', Tu: 'onb.dow.tu', W: 'onb.dow.w', Th: 'onb.dow.th',
  F: 'onb.dow.f', Sa: 'onb.dow.sa', Su: 'onb.dow.su',
};

const TIMEZONES = [
  { id: 'America/New_York',    label: 'EST (New York)' },
  { id: 'America/Chicago',     label: 'CST (Chicago)' },
  { id: 'America/Denver',      label: 'MST (Denver)' },
  { id: 'America/Los_Angeles', label: 'PST (Los Angeles)' },
  { id: 'Pacific/Honolulu',    label: 'HST (Honolulu)' },
];

// daily_lesson_time on the row is HH:MM:SS; the <input type="time"> wants HH:MM.
function toInput(serverTime: string | null | undefined): string {
  if (!serverTime) return '07:00';
  return serverTime.slice(0, 5);
}

function toServer(input: string): string {
  return input.length === 5 ? input + ':00' : input;
}

export function DailyLessonTimePanel() {
  const t = useT();
  const toast = useToast();
  const profile = useCurrentUserProfile();

  const [time, setTime] = useState('07:00');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [days, setDays] = useState<Set<string>>(new Set(['M', 'Tu', 'W', 'Th', 'F', 'Sa']));
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('09:00');
  const [quietEnd, setQuietEnd] = useState('18:00');
  const [saving, setSaving] = useState(false);

  // Hydrate from profile when it loads
  useEffect(() => {
    if (!profile.data) return;
    setTime(toInput(profile.data.daily_lesson_time));
    if (profile.data.timezone) setTimezone(profile.data.timezone);
    if (profile.data.delivery_days?.length) setDays(new Set(profile.data.delivery_days));
    setQuietEnabled(profile.data.quiet_enabled ?? false);
    setQuietStart(toInput(profile.data.quiet_start));
    setQuietEnd(toInput(profile.data.quiet_end));
  }, [profile.data]);

  function toggleDay(d: string) {
    setDays((cur) => {
      const next = new Set(cur);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    const { error } = await updateProfile({
      daily_lesson_time: toServer(time),
      timezone,
      delivery_days: Array.from(days),
      quiet_enabled: quietEnabled,
      quiet_start: quietEnabled ? toServer(quietStart) : null,
      quiet_end: quietEnabled ? toServer(quietEnd) : null,
    });
    setSaving(false);
    if (error) {
      toast(t('settings.profile.saveError', { message: error.message }), 'error');
    } else {
      toast(t('settings.profile.saved'), 'success');
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-ink-500 mb-2">
            {t('settings.delivery.time')}
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-ink-500 mb-2">
            {t('settings.delivery.timezone')}
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full h-11 px-3 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.id} value={tz.id}>
                {tz.label}
              </option>
            ))}
            {/* Render the saved tz even if not in our shortlist (e.g. user set
                it via onboarding to a tz outside the 5 we list). */}
            {!TIMEZONES.find((tz) => tz.id === timezone) && (
              <option value={timezone}>{timezone}</option>
            )}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-ink-500 mb-2">
          {t('settings.delivery.days')}
        </label>
        <div className="flex gap-1.5 flex-wrap">
          {DAYS.map((d) => {
            const selected = days.has(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(d)}
                className={`w-11 h-11 rounded-xl font-bold text-sm transition-colors ${
                  selected
                    ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                    : 'bg-bg-alt text-ink-700 border border-line-strong hover:border-navy-700'
                }`}
              >
                {t(DAY_KEYS[d])}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-ink-500 mt-2 leading-snug">
          {t('settings.delivery.daysHelp')}
        </p>
      </div>

      <div className="rounded-2xl border border-line bg-surface px-4 py-3">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div className="min-w-0 flex-1">
            <strong className="block text-sm">{t('settings.delivery.quiet')}</strong>
            <span className="text-xs text-ink-500">{t('settings.delivery.quietSub')}</span>
          </div>
          <span
            className={`relative inline-block w-10 h-6 rounded-full transition-colors shrink-0 ${
              quietEnabled ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line-strong'
            }`}
          >
            <input
              type="checkbox"
              checked={quietEnabled}
              onChange={(e) => setQuietEnabled(e.target.checked)}
              className="absolute opacity-0 w-full h-full cursor-pointer"
            />
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                quietEnabled ? 'translate-x-4' : ''
              }`}
            />
          </span>
        </label>

        {quietEnabled && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-line">
            <div>
              <label className="block text-[11px] font-bold text-ink-500 mb-1.5">
                {t('settings.delivery.quietStart')}
              </label>
              <input
                type="time"
                value={quietStart}
                onChange={(e) => setQuietStart(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-ink-500 mb-1.5">
                {t('settings.delivery.quietEnd')}
              </label>
              <input
                type="time"
                value={quietEnd}
                onChange={(e) => setQuietEnd(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border-[1.5px] border-line-strong bg-surface text-ink-900 focus:outline-none focus:border-navy-700 dark:focus:border-gold-500"
              />
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving || profile.loading}
        className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
      >
        {saving ? t('settings.profile.saving') : t('settings.delivery.save')}
      </button>
    </div>
  );
}
